import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import NotificationBell from "./NotificationBell";
import MobileBackBar from "./MobileBackBar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { MapPin, LogOut, ChevronDown, Store, User, HandCoins, Trophy, Menu, X } from "lucide-react";

function dashboardPath(role) {
  if (role === "admin") return "/admin";
  if (role === "customer") return "/mi-cuenta";
  if (role === "promoter") return "/promotor";
  return "/dashboard";
}

function dashboardLabel(role) {
  if (role === "admin") return "Panel Admin";
  if (role === "customer") return "Mi cuenta";
  if (role === "promoter") return "Mi panel";
  return "Mi negocio";
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate("/"); };

  return (
    <header className="glass-nav sticky top-0 z-40" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
        <Logo />
        <DesktopLinks />
        <div className="hidden md:flex items-center gap-2 lg:gap-3">
          {user
            ? <DesktopAuthed user={user} onGo={navigate} onLogout={handleLogout} />
            : <DesktopGuest onGo={navigate} />}
        </div>
        <MobileToggle user={user} open={mobileOpen} onToggle={() => setMobileOpen(v => !v)} />
      </div>

      {mobileOpen && (
        <MobileDrawer
          user={user}
          onGo={(p) => { setMobileOpen(false); navigate(p); }}
          onLogout={() => { setMobileOpen(false); handleLogout(); }}
        />
      )}
      <MobileBackBar />
    </header>
  );
}

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
      <div className="w-9 h-9 rounded-md bg-[hsl(var(--primary))] grid place-items-center shrink-0">
        <MapPin className="w-5 h-5 text-white" />
      </div>
      <div className="leading-tight">
        <div className="font-display text-lg sm:text-xl tracking-tight">OFERTAS</div>
        <div className="overline text-[10px] text-muted-foreground hidden sm:block">directorio local</div>
      </div>
    </Link>
  );
}

function DesktopLinks() {
  return (
    <nav className="hidden lg:flex items-center gap-7 text-sm">
      <Link to="/" className="hover:text-[hsl(var(--primary))]" data-testid="nav-home">Inicio</Link>
      <Link to="/directorios" className="hover:text-[hsl(var(--primary))]" data-testid="nav-directorios">Directorios</Link>
      <Link to="/ganadores" className="hover:text-amber-500 inline-flex items-center gap-1.5" data-testid="nav-ganadores">
        <Trophy className="w-3.5 h-3.5 text-amber-500" />
        <span>Ganadores</span>
      </Link>
      <Link to="/afilia-tu-negocio" className="hover:text-[hsl(var(--primary))] font-medium" data-testid="nav-afilia">Afilia tu negocio</Link>
    </nav>
  );
}

function DesktopAuthed({ user, onGo, onLogout }) {
  return (
    <>
      <NotificationBell />
      <Button variant="ghost" className="text-sm" onClick={() => onGo(dashboardPath(user.role))} data-testid="nav-dashboard-btn">
        {dashboardLabel(user.role)}
      </Button>
      <Button variant="outline" size="sm" onClick={onLogout} data-testid="nav-logout-btn">
        <LogOut className="w-4 h-4 mr-1" /> Salir
      </Button>
    </>
  );
}

function LoginDropdown({ onGo }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="gap-1 px-2 lg:px-4" data-testid="nav-login-trigger">
          <span className="hidden lg:inline">Iniciar sesión</span>
          <span className="lg:hidden">Entrar</span>
          <ChevronDown className="w-4 h-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">¿Cómo quieres entrar?</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onGo("/login-cliente")} data-testid="nav-login-cliente">
          <User className="w-4 h-4 mr-2 text-[#7C3AED]" />
          <div className="flex flex-col">
            <span className="font-medium">Soy cliente</span>
            <span className="text-[11px] text-muted-foreground">Acumulo puntos</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onGo("/login")} data-testid="nav-login-negocio">
          <Store className="w-4 h-4 mr-2 text-[#0E0E12]" />
          <div className="flex flex-col">
            <span className="font-medium">Soy comercio</span>
            <span className="text-[11px] text-muted-foreground">Administro mi anuncio</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onGo("/login-promotor")} data-testid="nav-login-promotor">
          <HandCoins className="w-4 h-4 mr-2 text-emerald-600" />
          <div className="flex flex-col">
            <span className="font-medium">Soy promotor</span>
            <span className="text-[11px] text-muted-foreground">Cobro mis comisiones</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DesktopGuest({ onGo }) {
  return (
    <>
      <LoginDropdown onGo={onGo} />
      <Button variant="outline" className="rounded-full border-[#C4B5FD] text-[#5B21B6] hover:bg-[#F5F3FF] px-3 lg:px-4 text-xs lg:text-sm" onClick={() => onGo("/registro-cliente")} data-testid="nav-customer-btn">
        Soy cliente
      </Button>
      <Button className="btn-primary rounded-full px-3 lg:px-5 text-xs lg:text-sm" onClick={() => onGo("/afilia-tu-negocio")} data-testid="nav-affiliate-btn">
        Afiliar negocio
      </Button>
    </>
  );
}

function MobileToggle({ user, open, onToggle }) {
  return (
    <div className="flex md:hidden items-center gap-1">
      {user && <NotificationBell />}
      <button
        type="button"
        onClick={onToggle}
        className="w-10 h-10 rounded-md grid place-items-center hover:bg-[#F5F3FF] transition-colors"
        aria-label={open ? "Cerrar menú" : "Abrir menú"}
        data-testid="nav-mobile-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>
    </div>
  );
}

function MobileDrawer({ user, onGo, onLogout }) {
  return (
    <div className="md:hidden border-t border-border bg-white" data-testid="nav-mobile-menu">
      <nav className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1 text-sm">
        <MobileLink onClick={() => onGo("/")} testid="mob-home">Inicio</MobileLink>
        <MobileLink onClick={() => onGo("/directorios")} testid="mob-directorios">Directorios</MobileLink>
        <MobileLink onClick={() => onGo("/ganadores")} testid="mob-ganadores" icon={<Trophy className="w-4 h-4 text-amber-500" />}>
          Ganadores
        </MobileLink>
        <MobileLink onClick={() => onGo("/afilia-tu-negocio")} testid="mob-afilia">Afilia tu negocio</MobileLink>

        {user ? <MobileAuthed user={user} onGo={onGo} onLogout={onLogout} /> : <MobileGuest onGo={onGo} />}
      </nav>
    </div>
  );
}

function MobileAuthed({ user, onGo, onLogout }) {
  return (
    <>
      <div className="h-px bg-border my-2" />
      <Button className="btn-primary rounded-full w-full justify-center" onClick={() => onGo(dashboardPath(user.role))} data-testid="mob-dashboard-btn">
        {dashboardLabel(user.role)}
      </Button>
      <Button variant="outline" className="w-full justify-center" onClick={onLogout} data-testid="mob-logout-btn">
        <LogOut className="w-4 h-4 mr-1" /> Cerrar sesión
      </Button>
    </>
  );
}

function MobileGuest({ onGo }) {
  return (
    <>
      <div className="h-px bg-border my-2" />
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground px-2 mt-1 mb-1">Entrar como</div>
      <MobileAction icon={<User className="w-4 h-4 text-[#7C3AED]" />} title="Soy cliente" subtitle="Acumulo puntos" onClick={() => onGo("/login-cliente")} testid="mob-login-cliente" />
      <MobileAction icon={<Store className="w-4 h-4 text-[#0E0E12]" />} title="Soy comercio" subtitle="Administro mi anuncio" onClick={() => onGo("/login")} testid="mob-login-negocio" />
      <MobileAction icon={<HandCoins className="w-4 h-4 text-emerald-600" />} title="Soy promotor" subtitle="Cobro mis comisiones" onClick={() => onGo("/login-promotor")} testid="mob-login-promotor" />

      <div className="h-px bg-border my-2" />
      <Button variant="outline" className="rounded-full border-[#C4B5FD] text-[#5B21B6] hover:bg-[#F5F3FF] w-full justify-center" onClick={() => onGo("/registro-cliente")} data-testid="mob-customer-btn">
        Soy cliente — registrarme
      </Button>
      <Button className="btn-primary rounded-full w-full justify-center" onClick={() => onGo("/afilia-tu-negocio")} data-testid="mob-affiliate-btn">
        Afiliar mi negocio
      </Button>
    </>
  );
}

function MobileLink({ onClick, children, icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-[#F5F3FF] active:bg-[#EDE9FE] flex items-center gap-2 text-zinc-800"
      data-testid={testid}
    >
      {icon}
      <span>{children}</span>
    </button>
  );
}

function MobileAction({ icon, title, subtitle, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-[#F5F3FF] active:bg-[#EDE9FE] flex items-center gap-3"
      data-testid={testid}
    >
      <span className="w-8 h-8 rounded-full bg-white border border-border grid place-items-center shrink-0">{icon}</span>
      <span className="flex flex-col">
        <span className="font-medium text-zinc-900">{title}</span>
        <span className="text-[11px] text-muted-foreground">{subtitle}</span>
      </span>
    </button>
  );
}
